import { type ChangeEvent, type KeyboardEvent, memo, useCallback, useEffect, useRef } from 'react';

import {
  workspaceAgentChoiceFreeTextField,
  workspaceAgentChoiceFreeTextInput,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentChoiceChip';
import {
  SOMETHING_ELSE_LABEL,
  SOMETHING_ELSE_MAX_LENGTH,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

export type WorkspaceAgentSomethingElseFieldProps = {
  onChange: (value: string) => void;
  onDismiss: () => void;
  value: string;
};

/** Pick several: Something else is one of the checked chips, and its text goes in a field under them. */
export const WorkspaceAgentSomethingElseField = memo(
  ({ onChange, onDismiss, value }: WorkspaceAgentSomethingElseFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, []);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
      },
      [onChange],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Escape' || event.metaKey || event.ctrlKey || event.shiftKey) {
          return;
        }

        event.preventDefault();
        onDismiss();
      },
      [onDismiss],
    );

    return (
      <div className={workspaceAgentChoiceFreeTextField()}>
        <input
          aria-label={SOMETHING_ELSE_LABEL}
          className={workspaceAgentChoiceFreeTextInput({ className: 'h-4' })}
          data-dock-local-escape=""
          data-testid="WORKSPACE_AGENT_SELECT_SOMETHING_ELSE_INPUT"
          maxLength={SOMETHING_ELSE_MAX_LENGTH}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Type your own answer"
          ref={inputRef}
          value={value}
        />
      </div>
    );
  },
);

WorkspaceAgentSomethingElseField.displayName = 'WorkspaceAgentSomethingElseField';
